import React, { useState, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../Auth/Auth';
import BreadCrumb from '../../BreadCrumb';
import useWeeklyReportData from './hooks/useWeeklyReportData';
import { getWeekStart, getWeekEnd, shiftWeek, formatWeekRange } from './utils/weekHelpers';
import { exportWeeklyReport } from './utils/excelExport';
import ReportTab from './tabs/ReportTab';
import BudgetReviewTab from './tabs/BudgetReviewTab';
import BreakdownTab from './tabs/BreakdownTab';
import SummaryTab from './tabs/SummaryTab';

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Weekly Management Report', to: '/analytics/weekly-report', current: true },
];

const TABS = [
  { key: 'report', label: 'Report' },
  { key: 'budget', label: 'Budget Review' },
  { key: 'breakdown', label: 'Breakdown' },
  { key: 'summary', label: 'Summary' },
];

const WeeklyManagementReport = () => {
  const { role, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('report');

  // Week navigation state — anchor is any date in the current week
  const [anchor, setAnchor] = useState(() => new Date());

  const weekStart = useMemo(() => getWeekStart(anchor), [anchor]);
  const weekEnd = useMemo(() => getWeekEnd(anchor), [anchor]);

  const { loading, data } = useWeeklyReportData(weekStart, weekEnd);

  const goBack = useCallback(() => setAnchor(prev => shiftWeek(prev, -1)), []);
  const goForward = useCallback(() => setAnchor(prev => shiftWeek(prev, 1)), []);

  const handleExport = useCallback(() => {
    if (!data) return;
    exportWeeklyReport(data, weekStart, weekEnd);
  }, [data, weekStart, weekEnd]);

  // Auth guard
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Admin' && role !== 'Finance') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <DocumentChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Weekly Management Report</h1>
                <p className="text-sm text-gray-600 mt-1">Comprehensive weekly financial overview</p>
              </div>
            </div>

            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={loading || !data}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md transition-all duration-200"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Week Navigator */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={goBack}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Previous week"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 min-w-[280px] text-center">
              {formatWeekRange(weekStart, weekEnd)}
            </h2>
            <button
              onClick={goForward}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Next week"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#6366F1" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading report data...</p>
            </div>
          </div>
        ) : data ? (
          <>
            {activeTab === 'report' && <ReportTab data={data} weekStart={weekStart} weekEnd={weekEnd} />}
            {activeTab === 'budget' && <BudgetReviewTab data={data} />}
            {activeTab === 'breakdown' && <BreakdownTab data={data} />}
            {activeTab === 'summary' && <SummaryTab data={data} />}
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <DocumentChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No data available for this week.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyManagementReport;
